let _lastPlanReloadAt = 0;

interface ClerkUser {
  id: string;
  reload: () => Promise<unknown>;
}

interface ClerkGlobal {
  session?: { getToken: (opts?: { template?: string }) => Promise<string | null> };
  user?: ClerkUser;
  openSignIn: () => void;
}

function getClerk(): ClerkGlobal | null {
  return (window as unknown as { Clerk?: ClerkGlobal }).Clerk ?? null;
}

export async function clerkFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const clerk = getClerk();
  const session = clerk?.session ?? null;
  const token = session ? await session.getToken() : null;
  const headers = new Headers(init.headers || {});
  if (token) headers.set("Authorization", `Bearer ${token}`);

  // The Bun host authenticates requests with the session JWT above (its azp
  // matches the host origin, which clerkAuth validates). But when the host
  // calls Convex AS THE USER — chat hydration (chats.byId) and per-message
  // commits — Convex only accepts the Clerk "convex"-template JWT (different
  // audience/claims). The session JWT is rejected there, which 404'd every
  // chat send and left chatMessages empty. Send the convex-template token in a
  // separate header so the host can use it for its Convex-as-user calls
  // without disturbing clerkAuth. getToken() caches per template, so the extra
  // call is cheap; if the "convex" template isn't configured the host falls
  // back to the session token.
  if (session) {
    try {
      const convexToken = await session.getToken({ template: "convex" });
      if (convexToken) headers.set("X-Convex-Token", convexToken);
    } catch {
      /* convex template unavailable — host falls back to the session token */
    }
  }

  const res = await fetch(input, { ...init, headers });

  if (res.status === 401) {
    // Only prompt for sign-in when there's actually no Clerk session. If the
    // user IS signed in and we still got 401, the backend rejected the token
    // (key mismatch, expired, etc.) — opening the sign-in modal in that case
    // just triggers Clerk's "cannotOpenSignInOrSignUp" dev notice and
    // confuses the user. Log and let the caller surface the error.
    if (!clerk?.user) {
      clerk?.openSignIn();
    } else {
      console.warn(
        `[clerkFetch] ${typeof input === "string" ? input : ""} returned 401 while signed in. Token rejected by backend.`
      );
    }
  } else if (res.status === 402) {
    if (Date.now() - _lastPlanReloadAt > 5000) {
      _lastPlanReloadAt = Date.now();
      await clerk?.user?.reload();
    }
  }
  return res;
}
